import type { PlayerIndex } from '@tpubg/shared';
import { sound } from '../lib/audio';
import type { CpuSetting } from '../lib/cpu';
import { prefersReducedMotion } from '../lib/motion';
import { createStore, shallowEqual } from '../lib/store';
import type { KeyboardController } from '../input/keyboard';
import { Particles } from './particles';
import type { ArcadeContext, ArcadeGameDef, PadInput, RoundEnd } from './types';

const DT = 1 / 60;
const ROUND_END_TICKS = 84;

export type ArcadePhase = 'countdown' | 'playing' | 'roundEnd' | 'matchEnd';
export type PauseReason = 'user' | 'focus' | null;

export interface ArcadeHud {
  phase: ArcadePhase;
  round: number;
  score0: number;
  score1: number;
  countdown: number | null;
  center: string | null;
  bannerWinner: PlayerIndex | null | undefined;
  bannerText: string | null;
  winner: PlayerIndex | null;
}

/** Fixed-timestep loop + match flow for any ArcadeGameDef. */
export class ArcadeRunner<S> {
  readonly hud: ReturnType<typeof createStore<ArcadeHud>>;
  readonly pause = createStore<PauseReason>(null);
  private readonly particles = new Particles(prefersReducedMotion());
  private readonly reduced = prefersReducedMotion();
  private canvas: HTMLCanvasElement | null = null;
  private g: CanvasRenderingContext2D | null = null;
  private scale = 1;
  private state!: S;
  private phase: ArcadePhase = 'countdown';
  private phaseTicks = 0;
  private round = 0;
  private scores: [number, number] = [0, 0];
  private banner: RoundEnd | null = null;
  private winner: PlayerIndex | null = null;
  private shakeAmt = 0;
  private acc = 0;
  private last = 0;
  private time = 0;
  private raf = 0;
  private lastCount = -1;
  /** While paused the picture doesn't change, so it is drawn once and then left alone. */
  private pausedDrawn = false;
  private cpuMemory: Record<string, unknown> = {};
  private readonly ctx: ArcadeContext;

  constructor(
    private readonly def: ArcadeGameDef<S>,
    private readonly keyboard: KeyboardController,
    private readonly cpu: CpuSetting | null = null,
  ) {
    this.hud = createStore<ArcadeHud>(this.makeHud(), shallowEqual);
    this.ctx = {
      play: (n) => sound.play(n),
      particles: this.particles,
      reducedMotion: this.reduced,
      shake: (a) => {
        if (!this.reduced) this.shakeAmt = Math.max(this.shakeAmt, a);
      },
    };
    this.restart();
  }

  get acceptingInput(): boolean {
    return this.pause.get() === null && this.phase !== 'matchEnd';
  }

  setCanvas(canvas: HTMLCanvasElement | null): void {
    this.canvas = canvas;
    this.g = canvas?.getContext('2d') ?? null;
  }

  resize(cssWidth: number): void {
    if (!this.canvas) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = Math.max(1, Math.round(cssWidth * dpr));
    const h = Math.max(1, Math.round((cssWidth * dpr * this.def.height) / this.def.width));
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w;
      this.canvas.height = h;
      this.pausedDrawn = false;
    }
    this.scale = w / this.def.width;
  }

  start(): void {
    this.last = performance.now();
    document.addEventListener('visibilitychange', this.onHidden);
    window.addEventListener('blur', this.onBlur);
    this.raf = requestAnimationFrame(this.frame);
  }

  stop(): void {
    cancelAnimationFrame(this.raf);
    document.removeEventListener('visibilitychange', this.onHidden);
    window.removeEventListener('blur', this.onBlur);
  }

  restart(): void {
    this.scores = [0, 0];
    this.round = 0;
    this.winner = null;
    this.particles.clear();
    this.keyboard.clear();
    this.pause.set(null);
    this.nextRound();
  }

  setPaused(reason: PauseReason): void {
    if (reason !== null && this.phase === 'matchEnd') return;
    this.keyboard.clear();
    this.acc = 0;
    this.pause.set(reason);
  }

  togglePause(): void {
    this.setPaused(this.pause.get() ? null : 'user');
  }

  private nextRound(): void {
    this.round++;
    this.state = this.def.createRound(this.round);
    this.phase = 'countdown';
    this.phaseTicks = this.round === 1 ? 180 : Math.round((this.def.betweenRounds ?? 1) * 60);
    this.banner = null;
    this.lastCount = -1;
    this.cpuMemory = {};
    this.pushHud();
  }

  private readonly onHidden = () => {
    if (document.hidden) this.setPaused('focus');
  };

  private readonly onBlur = () => {
    if (this.pause.get() === null) this.setPaused('focus');
  };

  private tick(): void {
    const inputs = this.readInputs();
    switch (this.phase) {
      case 'countdown': {
        if (this.round === 1) {
          const n = Math.ceil(this.phaseTicks / 60);
          if (n !== this.lastCount) {
            this.lastCount = n;
            sound.play('tick');
          }
        }
        if (--this.phaseTicks <= 0) {
          this.phase = 'playing';
          if (this.round === 1) sound.play('go');
        }
        break;
      }
      case 'playing': {
        const result = this.def.step(this.state, inputs, this.ctx);
        if (result) {
          this.banner = result;
          if (result.winner !== null) this.scores[result.winner] += Math.max(1, Math.round(result.points ?? 1));
          this.phase = 'roundEnd';
          this.phaseTicks = ROUND_END_TICKS;
          sound.play('round');
        }
        break;
      }
      case 'roundEnd':
        if (--this.phaseTicks <= 0) {
          const best = Math.max(...this.scores);
          if (best >= this.def.winScore) {
            this.phase = 'matchEnd';
            this.winner = this.scores[0] > this.scores[1] ? 0 : 1;
            sound.play('match');
          } else {
            this.nextRound();
          }
        }
        break;
      case 'matchEnd':
        break;
    }
  }

  /** Two humans share the keyboard, or Player 1 (either key set) faces the computer as Player 2. */
  private readInputs(): readonly [PadInput, PadInput] {
    const cpu = this.cpu;
    if (!cpu || !this.def.cpu) return [this.keyboard.pad(0), this.keyboard.pad(1)];
    const human = this.keyboard.padAny();
    const machine =
      this.phase === 'playing' ? this.def.cpu(this.state, cpu.player, cpu.level, this.cpuMemory) : { up: false, down: false, left: false, right: false, action: false, actionPressed: false };
    return cpu.player === 1 ? [human, machine] : [machine, human];
  }

  private makeHud(): ArcadeHud {
    return {
      phase: this.phase,
      round: this.round,
      score0: this.scores?.[0] ?? 0,
      score1: this.scores?.[1] ?? 0,
      countdown: this.phase === 'countdown' && this.round === 1 ? Math.ceil(this.phaseTicks / 60) : null,
      center: this.state !== undefined && this.def.hud ? this.def.hud(this.state) : null,
      bannerWinner: this.banner ? this.banner.winner : undefined,
      bannerText: this.banner?.text ?? null,
      winner: this.winner,
    };
  }

  private pushHud(): void {
    this.hud?.set(this.makeHud());
  }

  private readonly frame = (now: number): void => {
    this.raf = requestAnimationFrame(this.frame);
    const dt = Math.min(0.1, (now - this.last) / 1000);
    this.last = now;
    if (this.pause.get() === null) {
      this.time += dt;
      this.acc += dt;
      let steps = 0;
      while (this.acc >= DT && steps < 6) {
        this.tick();
        this.acc -= DT;
        steps++;
      }
      if (this.acc > DT) this.acc = 0;
      this.particles.update(dt);
      this.shakeAmt = Math.max(0, this.shakeAmt - dt * 40);
      this.pushHud();
      this.pausedDrawn = false;
    } else if (this.pausedDrawn) return;
    else this.pausedDrawn = true;
    this.draw();
  };

  private draw(): void {
    const g = this.g;
    if (!g || !this.canvas) return;
    const s = this.scale;
    const sx = this.shakeAmt ? (Math.random() - 0.5) * this.shakeAmt : 0;
    const sy = this.shakeAmt ? (Math.random() - 0.5) * this.shakeAmt : 0;
    g.setTransform(1, 0, 0, 1, 0, 0);
    g.fillStyle = '#15181D';
    g.fillRect(0, 0, this.canvas.width, this.canvas.height);
    g.setTransform(s, 0, 0, s, sx * s, sy * s);
    g.save();
    this.def.render(g, this.state, { time: this.time, reducedMotion: this.reduced, phase: this.phase });
    g.restore();
    this.particles.draw(g);
    g.globalAlpha = 1;
  }
}
